/*
Copyright (C) 2026  Integrated Tech9labs Pvt. Ltd.
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

/*
 * Cut-over request helpers shared by the Change control page, the request dialog, the transfer's
 * "Upcoming cut-over" card and the deployment's rollback card. Pure functions, so they are tested
 * without a browser (cutoverUtils.spec.ts).
 */

import {
  Ban,
  CalendarClock,
  CircleCheck,
  CircleX,
  Clock,
  Hourglass,
  LoaderCircle,
  ThumbsUp,
  TimerOff,
  type LucideIcon,
} from "lucide-react";

import { parseApiDate } from "@src/components/ui/Lists/ListTable";
import type { PillTone } from "@src/components/ui/Lists/ListTable";
import type { CutoverRequest, CutoverStatus } from "@src/@types/Cutover";

export const ACTIVE_STATUSES: CutoverStatus[] = ["pending_approval", "approved", "scheduled", "started"];
export const FINAL_STATUSES: CutoverStatus[] = ["completed", "failed", "rejected", "cancelled", "expired"];
export const CANCELLABLE_STATUSES: CutoverStatus[] = ["pending_approval", "approved", "scheduled"];

export const STATUS_INFO: {
  [s in CutoverStatus]: { label: string; tone: PillTone; icon: LucideIcon; spin?: boolean };
} = {
  pending_approval: { label: "Waiting for approval", tone: "warning", icon: Hourglass },
  approved: { label: "Approved", tone: "brand", icon: ThumbsUp },
  scheduled: { label: "Scheduled", tone: "info", icon: CalendarClock },
  started: { label: "Deploying", tone: "info", icon: LoaderCircle, spin: true },
  completed: { label: "Completed", tone: "success", icon: CircleCheck },
  failed: { label: "Failed", tone: "danger", icon: CircleX },
  rejected: { label: "Rejected", tone: "danger", icon: Ban },
  cancelled: { label: "Cancelled", tone: "neutral", icon: CircleX },
  expired: { label: "Expired", tone: "neutral", icon: TimerOff },
};

export const statusInfo = (status?: string | null) =>
  STATUS_INFO[status as CutoverStatus] || { label: status || "Unknown", tone: "neutral" as PillTone, icon: Clock };

export const isActive = (r?: CutoverRequest | null) => Boolean(r && ACTIVE_STATUSES.includes(r.status));

/** The active request of a transfer: `cutover_requests` of GET /transfers/{id}, newest window first. */
export const activeRequest = (requests?: CutoverRequest[] | null): CutoverRequest | null =>
  (requests || []).filter(isActive).sort((a, b) => (b.window_start || "").localeCompare(a.window_start || ""))[0] || null;

export const requesterName = (r: CutoverRequest) =>
  r.requested_by?.name || r.user_name || (r.user_id ? r.user_id.slice(0, 8) : "someone");

export const requesterId = (r: CutoverRequest) => r.requested_by?.id || r.user_id;

type UserLike = { id?: string; name?: string; isAdmin?: boolean | null } | null | undefined;

/** Whether this user created the request (the four-eyes rule forbids deciding on it). */
export const isOwnRequest = (r: CutoverRequest, user: UserLike) =>
  Boolean(user && ((user.id && requesterId(r) === user.id) || (!r.user_id && user.name && requesterName(r) === user.name)));

/**
 * Approve / reject are for users who hold `migration:cutover_requests:approve` (default: admin),
 * never on their own requests. `allowSelfApproval` mirrors `[cutover] allow_self_approval`.
 */
export const decisionState = (
  r: CutoverRequest,
  user: UserLike,
  allowSelfApproval = false,
): { canDecide: boolean; reason: string | null } => {
  if (r.status !== "pending_approval") return { canDecide: false, reason: null };
  if (!user?.isAdmin) return { canDecide: false, reason: "Only an administrator can approve or reject cut-over requests." };
  if (!allowSelfApproval && isOwnRequest(r, user)) {
    return { canDecide: false, reason: "You can't approve your own request. Someone else must approve or reject it." };
  }
  return { canDecide: true, reason: null };
};

export const canCancel = (r: CutoverRequest, user: UserLike) =>
  CANCELLABLE_STATUSES.includes(r.status) && Boolean(user && (user.isAdmin || isOwnRequest(r, user)));

/** Waiting for this user's decision: pending, someone else's, and the user may decide. */
export const waitingForMe = (r: CutoverRequest, user: UserLike) => decisionState(r, user).canDecide;

// ------------------------------------------------------------------ time

const pad = (n: number) => String(n).padStart(2, "0");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** The browser's time zone ("Asia/Kolkata"), for labels. */
export const localZone = () => {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone || "local time";
    // Some ICU builds still report India's old name.
    return zone === "Asia/Calcutta" ? "Asia/Kolkata" : zone;
  } catch (e) {
    return "local time";
  }
};

/** "GMT+5:30" for a date in the browser's zone. */
export const localOffsetLabel = (d: Date = new Date()) => {
  const m = -d.getTimezoneOffset();
  const sign = m >= 0 ? "+" : "-";
  const a = Math.abs(m);
  return `GMT${sign}${Math.floor(a / 60)}${a % 60 ? `:${pad(a % 60)}` : ""}`;
};

/** A Date as the value of <input type="datetime-local"> ("2026-10-04T18:30", local time). */
export const toLocalInput = (d: Date | null) =>
  d ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}` : "";

/** <input type="datetime-local"> value (local time) to a Date; null when empty or invalid. */
export const fromLocalInput = (value: string): Date | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value || "");
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), Number(m[4]), Number(m[5]), 0, 0);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** The UTC ISO time the API receives ("2026-10-04T13:00:00Z"). */
export const toApiUtc = (d: Date) => `${d.toISOString().slice(0, 19)}Z`;

/** "Sat 04 Oct, 18:30" (local). */
export const formatWhen = (value?: string | Date | null, withYear = false) => {
  const d = value instanceof Date ? value : parseApiDate(value || null);
  if (!d) return "";
  return `${DAYS[d.getDay()]} ${pad(d.getDate())} ${MONTHS[d.getMonth()]}${withYear ? ` ${d.getFullYear()}` : ""}, ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/** "04 Oct 2026, 13:00 UTC". */
export const formatUtc = (value?: string | Date | null) => {
  const d = value instanceof Date ? value : parseApiDate(value || null);
  if (!d) return "";
  return `${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}, ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`;
};

/** "Sat 04 Oct, 18:30 – 22:30" or "Sat 04 Oct, 22:00 – Sun 05 Oct, 02:00" (local). */
export const formatWindow = (start?: string | null, end?: string | null) => {
  const s = parseApiDate(start || null);
  const e = parseApiDate(end || null);
  if (!s || !e) return "";
  const sameDay = s.toDateString() === e.toDateString();
  return `${formatWhen(s)} – ${sameDay ? `${pad(e.getHours())}:${pad(e.getMinutes())}` : formatWhen(e)}`;
};

/** "4 h", "1 h 30 min", "2 d 3 h", "45 min". */
export const formatDuration = (ms: number) => {
  const totalMin = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d) return h ? `${d} d ${h} h` : `${d} d`;
  if (h) return m ? `${h} h ${m} min` : `${h} h`;
  return `${m} min`;
};

/** "in 3 h", "in 2 d 4 h", "2 h ago", "now". */
export const relative = (value?: string | Date | null, now = Date.now()) => {
  const d = value instanceof Date ? value : parseApiDate(value || null);
  if (!d) return "";
  const diff = d.getTime() - now;
  if (Math.abs(diff) < 60000) return "now";
  return diff > 0 ? `in ${formatDuration(diff)}` : `${formatDuration(-diff)} ago`;
};

/** Where a request stands against its window. */
export const windowPhase = (r: CutoverRequest, now = Date.now()): "before" | "open" | "ended" => {
  const s = parseApiDate(r.window_start)?.getTime() ?? 0;
  const e = parseApiDate(r.window_end)?.getTime() ?? 0;
  if (now < s) return "before";
  if (now < e) return "open";
  return "ended";
};

export type WindowError = { start?: string; end?: string };

/** The create form's window check, as the API does it: end after start, end in the future. */
export const validateWindow = (start: Date | null, end: Date | null, now = Date.now()): WindowError => {
  const out: WindowError = {};
  if (!start) out.start = "Choose when the window opens.";
  if (!end) out.end = "Choose when the window closes.";
  if (start && end && end.getTime() <= start.getTime()) out.end = "The window must close after it opens.";
  else if (end && end.getTime() <= now) out.end = "The window must close in the future.";
  return out;
};

/** Window presets for the request dialog: tonight 22:00-02:00, Saturday 18:00-22:00, next 4 hours. */
export const windowPresets = (now = new Date()) => {
  const at = (base: Date, days: number, h: number, m = 0) => {
    const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + days, h, m, 0, 0);
    return d;
  };
  const tonightStart = at(now, now.getHours() >= 22 ? 1 : 0, 22);
  const daysToSat = (6 - now.getDay() + 7) % 7 || 7;
  const satStart = at(now, daysToSat, 18);
  const soon = new Date(Math.ceil((now.getTime() + 15 * 60000) / (15 * 60000)) * 15 * 60000);
  return [
    { id: "tonight", label: "Tonight 22:00 – 02:00", start: tonightStart, end: new Date(tonightStart.getTime() + 4 * 3600000) },
    { id: "saturday", label: `Sat ${pad(satStart.getDate())} ${MONTHS[satStart.getMonth()]} 18:00 – 22:00`, start: satStart, end: new Date(satStart.getTime() + 4 * 3600000) },
    { id: "now", label: "Next 4 hours", start: soon, end: new Date(soon.getTime() + 4 * 3600000) },
  ];
};

// ------------------------------------------------------------------ timeline

export type TimelineEvent = {
  key: string;
  at: string | null;
  title: string;
  who?: string | null;
  comment?: string | null;
  tone: "brand" | "success" | "danger" | "warning" | "neutral" | "info";
  pending?: boolean;
};

/** The request's history for the details drawer: created, decisions, scheduled/started, the end. */
export const buildTimeline = (r: CutoverRequest, now = Date.now()): TimelineEvent[] => {
  const events: TimelineEvent[] = [
    {
      key: "created",
      at: r.created_at,
      title: "Requested",
      who: requesterName(r),
      comment: r.reason || null,
      tone: "brand",
    },
  ];
  (r.approvals || [])
    .slice()
    .sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""))
    .forEach(a =>
      events.push({
        key: `approval-${a.id}`,
        at: a.created_at,
        title: a.decision === "approved" ? "Approved" : "Rejected",
        who: a.user_name || a.user_id?.slice(0, 8),
        comment: a.comment || null,
        tone: a.decision === "approved" ? "success" : "danger",
      }),
    );
  const phase = windowPhase(r, now);
  if (r.status === "pending_approval") {
    events.push({ key: "waiting", at: null, title: "Waiting for approval", tone: "warning", pending: true });
  }
  if (["approved", "scheduled"].includes(r.status) || ((r.started_at || r.deployment_id) && r.approved_at)) {
    events.push({
      key: "scheduled",
      at: r.approved_at || null,
      title: phase === "before" && !r.started_at ? `Scheduled for ${formatWhen(r.window_start)}` : "Scheduled",
      tone: "info",
    });
  }
  if (r.started_at || r.deployment_id) {
    events.push({ key: "started", at: r.started_at || null, title: "Deployment started", who: "SwiftIQ", tone: "info" });
  } else if (["approved", "scheduled"].includes(r.status)) {
    events.push({ key: "start", at: null, title: `SwiftIQ starts the deployment ${relative(r.window_start, now)}`, tone: "neutral", pending: true });
  }
  const endTitles: { [s: string]: [string, TimelineEvent["tone"]] } = {
    completed: ["Deployment completed", "success"],
    failed: ["Failed", "danger"],
    cancelled: ["Cancelled", "neutral"],
    expired: ["Expired: the window closed first", "neutral"],
  };
  if (endTitles[r.status]) {
    events.push({
      key: "end",
      at: r.finished_at || r.updated_at || null,
      title: endTitles[r.status][0],
      comment: r.status_reason || null,
      tone: endTitles[r.status][1],
    });
  } else if (r.status === "started") {
    events.push({ key: "running", at: null, title: "Deploying…", tone: "info", pending: true });
  }
  return events;
};

/** "Every VM" or the instance list. */
export const instancesLabel = (r: CutoverRequest, all?: string[]) => {
  if (!r.instances || !r.instances.length) return all && all.length ? `All ${all.length === 1 ? "" : `${all.length} `}VMs` : "Every VM";
  return r.instances.map(shortInstance).join(", ");
};

/** VMware instance names carry their datacenter ("KES-DC-PROD/kes-app-01"). */
export const shortInstance = (name: string) => (name.includes("/") ? name.split("/").pop() || name : name);
